import { ArrowLeft, LogOut, Sprout } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { LanguageToggle } from "../components/LanguageToggle";
import { LoginScreen } from "../components/LoginScreen";
import { ReadPanel } from "../components/ReadPanel";
import {
  createLink,
  createOntologyType,
  createReadAnchor,
  createRelation,
  createRelationType,
  createSource,
  deleteLink,
  deleteReadAnchor,
  loadBootstrap,
  loadLinks,
  loadReadAnchors,
  loadSession,
  loginWithCredentials,
  loginWithPasscode,
  logoutSession,
  registerAccount,
  resolveReadUrl,
  updateOntologyType,
  updateRelationType,
  updateSource
} from "../lib/api";
import type { EntityLink, OntologyType, NoteCard, ReadAnchor, RelationType, SourceNode } from "../types";

type Bootstrap = {
  sourceNodes: SourceNode[];
  notes: NoteCard[];
  ontologyTypes: OntologyType[];
  relationTypes: RelationType[];
  mode?: string;
};

export function Read() {
  const navigate = useNavigate();
  const { sourceId } = useParams();
  const [data, setData] = useState<Bootstrap>({ sourceNodes: [], notes: [], ontologyTypes: [], relationTypes: [], mode: "initial" });
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [loginError, setLoginError] = useState("");
  const [sourceError, setSourceError] = useState("");
  const [linkError, setLinkError] = useState("");
  const [relationError, setRelationError] = useState("");
  const [readError, setReadError] = useState("");
  const [ontologyError, setOntologyError] = useState("");
  const [linksByObject, setLinksByObject] = useState<Record<string, EntityLink[]>>({});
  const [anchorsBySource, setAnchorsBySource] = useState<Record<string, ReadAnchor[]>>({});

  const refreshData = async () => {
    const payload = await loadBootstrap();
    setData(payload);
    return payload;
  };

  useEffect(() => {
    let isMounted = true;
    loadSession()
      .then(async (session) => {
        if (!isMounted) return;
        if (session.authenticated) {
          await refreshData();
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
        }
      })
      .finally(() => {
        if (isMounted) setIsCheckingAuth(false);
      });
    return () => {
      isMounted = false;
    };
  }, []);

  const completeAuth = async () => {
    await refreshData();
    setIsAuthenticated(true);
  };
  const handleLoginCredentials = async (input: { email: string; password: string }) => {
    setLoginError("");
    try {
      await loginWithCredentials(input);
      await completeAuth();
    } catch (error) {
      setLoginError(error instanceof Error ? error.message : "Đăng nhập thất bại");
      throw error;
    }
  };
  const handleRegister = async (input: { email: string; username: string; password: string }) => {
    setLoginError("");
    try {
      await registerAccount(input);
      await completeAuth();
    } catch (error) {
      setLoginError(error instanceof Error ? error.message : "Đăng ký thất bại");
      throw error;
    }
  };
  const handleLoginPasscode = async (passcode: string) => {
    setLoginError("");
    try {
      await loginWithPasscode(passcode);
      await completeAuth();
    } catch (error) {
      setLoginError(error instanceof Error ? error.message : "Login failed");
      throw error;
    }
  };
  const handleLogout = async () => {
    await logoutSession();
    setIsAuthenticated(false);
  };

  const handleLoadLinks = async (objectId: string) => {
    setLinkError("");
    try {
      const payload = await loadLinks(objectId);
      setLinksByObject((current) => ({ ...current, [objectId]: payload.data ?? [] }));
    } catch (error) {
      setLinkError(error instanceof Error ? error.message : "Load links failed");
    }
  };
  const handleLoadAnchors = async (sourceId: string) => {
    setReadError("");
    try {
      const payload = await loadReadAnchors(sourceId);
      setAnchorsBySource((current) => ({ ...current, [sourceId]: payload.data ?? [] }));
    } catch (error) {
      setReadError(error instanceof Error ? error.message : "Load anchors failed");
    }
  };

  if (isCheckingAuth) {
    return <div className="flex min-h-screen items-center justify-center bg-paper text-sm font-bold text-muted">Đang tải Link & trích</div>;
  }
  if (!isAuthenticated) {
    return (
      <LoginScreen onLoginCredentials={handleLoginCredentials} onRegister={handleRegister} onLoginPasscode={handleLoginPasscode} error={loginError} />
    );
  }

  return (
    <div className="min-h-screen bg-paper text-ink">
      <header className="sticky top-0 z-20 border-b border-line bg-white/95 backdrop-blur">
        <div className="mx-auto flex min-h-16 max-w-[1560px] flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-blue-100 bg-blue-50 text-brand">
              <Sprout size={21} aria-hidden="true" />
            </div>
            <div className="min-w-0">
              <p className="truncate text-base font-bold leading-tight">Link & trích</p>
              <p className="hidden truncate text-xs font-medium text-muted sm:block">Quản lý link, YouTube, quote và note theo source</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <LanguageToggle />
            <button
              className="inline-flex h-10 items-center gap-2 rounded-lg border border-line bg-white px-3 text-sm font-bold text-slate-700 hover:bg-slate-50"
              type="button"
              onClick={() => navigate("/?tab=library")}
            >
              <ArrowLeft size={17} aria-hidden="true" />
              <span className="hidden sm:inline">Thư viện</span>
            </button>
            <button
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-line bg-white text-slate-600 hover:bg-slate-50"
              type="button"
              aria-label="Sign out"
              onClick={handleLogout}
            >
              <LogOut size={18} aria-hidden="true" />
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1560px] px-4 py-4 sm:px-6">
        <ReadPanel
          sources={data.sourceNodes}
          notes={data.notes}
          linksByObject={linksByObject}
          anchorsBySource={anchorsBySource}
          ontologyTypes={data.ontologyTypes}
          relationTypes={data.relationTypes}
          sourceError={sourceError}
          linkError={linkError}
          readError={readError}
          relationError={relationError}
          ontologyError={ontologyError}
          onCreateSource={async (input) => {
            setSourceError("");
            try {
              const result = await createSource(input);
              await refreshData();
              return (result.data?.sourceId ?? result.data?.id) as string | undefined;
            } catch (error) {
              setSourceError(error instanceof Error ? error.message : "Create source failed");
              throw error;
            }
          }}
          onUpdateSource={async (id, input) => {
            setSourceError("");
            try {
              await updateSource(id, input);
              await refreshData();
            } catch (error) {
              setSourceError(error instanceof Error ? error.message : "Update source failed");
              throw error;
            }
          }}
          onCreateLink={async (objectId, input) => {
            setLinkError("");
            await createLink({ objectId, ...input });
            await handleLoadLinks(objectId);
          }}
          onDeleteLink={async (objectId, linkId) => {
            setLinkError("");
            await deleteLink(linkId);
            await handleLoadLinks(objectId);
          }}
          onCreateRelation={async (input) => {
            setRelationError("");
            try {
              await createRelation(input);
              await refreshData();
            } catch (error) {
              setRelationError(error instanceof Error ? error.message : "Create relation failed");
              throw error;
            }
          }}
          onLoadLinks={handleLoadLinks}
          onResolveUrl={resolveReadUrl}
          onLoadAnchors={handleLoadAnchors}
          onCreateAnchor={async (sourceId, input) => {
            setReadError("");
            try {
              await createReadAnchor(sourceId, input);
              await refreshData();
              await handleLoadAnchors(sourceId);
            } catch (error) {
              setReadError(error instanceof Error ? error.message : "Create anchor failed");
              throw error;
            }
          }}
          onDeleteAnchor={async (sourceId, anchorId) => {
            setReadError("");
            await deleteReadAnchor(anchorId);
            await handleLoadAnchors(sourceId);
          }}
          onCreateType={async (input) => {
            setOntologyError("");
            try {
              await createOntologyType(input);
              await refreshData();
            } catch (error) {
              setOntologyError(error instanceof Error ? error.message : "Create type failed");
              throw error;
            }
          }}
          onUpdateType={async (id, input) => {
            setOntologyError("");
            await updateOntologyType(id, input);
            await refreshData();
          }}
          onCreateRelationType={async (input) => {
            setRelationError("");
            await createRelationType(input);
            await refreshData();
          }}
          onUpdateRelationType={async (id, input) => {
            setRelationError("");
            await updateRelationType(id, input);
            await refreshData();
          }}
          initialSourceId={sourceId}
          onSelectSource={(id) => navigate(`/read/${encodeURIComponent(id)}`, { replace: true })}
        />
      </main>
    </div>
  );
}
